import { Flex } from "@chakra-ui/react";
import { HorizontalGreyBlackField } from "components/DataDisplay/GreyBlackField";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { longAttributeStyles } from "components/DataDisplay/styles";
import { useCallback, useEffect, useMemo } from "react";
import { useV2PathListQuery } from "features/Path/hooks";
import { Path, PathId } from "features/Path/models";
import SelectPathsMenuButton from "features/Path/Components/Button/SelectPathsMenuButton";
import BasePathTag, { PathTag, PrebuiltPathTag } from "components/Tag/BasePathTag";
import { useSearchParams } from "react-router";

interface AddPathsFieldProps {
  form: UseFormReturn<any>;
  includeAdd?: boolean
  removeable?: boolean
  setInitialPathByQueryParam?: boolean
}

export default function AddPathsField({ form, setInitialPathByQueryParam = false, includeAdd = true, removeable = true }: AddPathsFieldProps) {
  // This is not dangerous, but it queries all paths. If a company has too many, this could become a problem
  // though I do not forsee companies to have more than 10
  const { data: paths } = useV2PathListQuery("");
  const { control } = form;
  const [searchParams, setSearchParams] = useSearchParams();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "paths",
    // defaults to inserting an 'id' for every field, which overlaps w/ our object ids
    keyName: "fieldId",
  });

  // Tried to make this into its own hook, but it was giving me errors so defining it here (I think due to
  // passing the append to a subcomponent). This will look at the query params, and if a newPathId exists,
  // and no paths are already defined, it will add this path as the first
  useEffect(() => {
    const newPathId = searchParams.get("newPathId");
    const watchedPaths = form.watch("paths");
    // will prevent being added to templates with paths already
    const isAlreadyEmpty = watchedPaths?.length === 0;

    if (setInitialPathByQueryParam && newPathId && isAlreadyEmpty) {
      append({ id: newPathId });
    }
    if (newPathId && watchedPaths.map((p) => p.id).includes(newPathId)) {
      searchParams.delete("newPathId");
      setSearchParams(searchParams);
    }
  }, [append, fields, form, setInitialPathByQueryParam, searchParams, setSearchParams]);

  const addPathId = useCallback((pathId: string) => append({ id: pathId }), [append]);

  const selectedPathsComponents = useMemo(() => (fields || []).map((field, index) => {
    const path = paths?.find((p: Path) => p.id === field.id);
    return <BasePathTag key={field.fieldId} pathId={path?.id} pathName={path?.name} icon={path?.icon} iconColor={path?.iconColor} removeable={removeable} onRemove={() => remove(index)} />;
  }), [fields, paths, remove, removeable]);

  const selectedPathIds = useMemo(() => (fields || []).map((field) => field.id), [fields]);

  return (
    <HorizontalGreyBlackField label="Paths" labelContainerProps={longAttributeStyles.labelContainerProps}>
      <Flex gap="1" align="center" wrap="wrap">
        {selectedPathsComponents}
        {includeAdd && (
          <SelectPathsMenuButton selectedPathIds={selectedPathIds} addPathId={addPathId} />
        )}
      </Flex>
    </HorizontalGreyBlackField>
  );
}

export function ShowPaths({ pathIds }: { pathIds: PathId[] }) {
  return (
    <HorizontalGreyBlackField label="Paths" labelContainerProps={longAttributeStyles.labelContainerProps}>
      <Flex gap="1" align="center" wrap="wrap">
        {pathIds.map((pathId) => (
          <PathTag key={pathId} pathId={pathId} removeable={false} />
        ))}
      </Flex>
    </HorizontalGreyBlackField>
  );
}

export function ShowPrebuiltPaths({ prebuiltPathIds }: { prebuiltPathIds: PathId[] }) {
  return (
    <HorizontalGreyBlackField label="Paths" labelContainerProps={longAttributeStyles.labelContainerProps}>
      <Flex gap="1" align="center" wrap="wrap">
        {prebuiltPathIds.map((pathId) => (
          <PrebuiltPathTag key={pathId} pathId={pathId} removeable={false} isPrebuilt />
        ))}
      </Flex>
    </HorizontalGreyBlackField>
  );
}
